import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  User,
  Settings,
  CreditCard,
  Key,
  BarChart3,
  Download,
  LogOut,
  Sparkles,
  Zap,
  Clock,
  TrendingUp,
  Calendar,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  ChevronRight,
  Check,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/context/AuthContext';
import { logOut, updateUserProfile } from '@/services/auth';
import { DashboardBackground } from '@/components/ui/DashboardBackground';

// Usage data from Firestore (will be real data when connected)
interface UsageData {
  completions: {
    used: number;
    limit: string;
    trend: string;
  };
  agentRequests: {
    used: number;
    limit: number;
    trend: string;
  };
  timeSaved: string;
  linesWritten: string;
}

interface ActivityItem {
  date: string;
  action: string;
  time: string;
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const tabs = [
    { id: 'overview', label: t('dashboard.tabs.overview'), icon: BarChart3 },
    { id: 'usage', label: t('dashboard.tabs.usage'), icon: Zap },
    { id: 'settings', label: t('dashboard.tabs.settings'), icon: Settings },
    { id: 'api-keys', label: t('dashboard.tabs.apiKeys'), icon: Key },
    { id: 'billing', label: t('dashboard.tabs.billing'), icon: CreditCard },
  ];
  const [activeTab, setActiveTab] = useState('overview');
  const [showApiKey, setShowApiKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({
    displayName: '',
  });

  // Usage data - can be fetched from Firestore
  const [usageData] = useState<UsageData>({
    completions: {
      used: 0,
      limit: 'Unlimited',
      trend: '+0%',
    },
    agentRequests: {
      used: 0,
      limit: 500,
      trend: '+0%',
    },
    timeSaved: '0 hours',
    linesWritten: '0',
  });

  // Activity data - can be fetched from Firestore
  const [activityData] = useState<ActivityItem[]>([
    { date: 'Today', action: 'Welcome to Ochtopus!', time: 'Just now' },
  ]);

  // Update form when user data loads
  useEffect(() => {
    if (user) {
      setProfileForm({
        displayName: user.displayName || '',
      });
    }
  }, [user]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/', { state: { requireAuth: true } });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="pt-24 min-h-screen bg-bg-base flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-4 text-accent" size={48} />
          <p className="text-text-secondary">{t('dashboard.loading')}</p>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated
  if (!isAuthenticated || !user) {
    return null;
  }

  const handleSignOut = async () => {
    try {
      await logOut();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleSaveProfile = async () => {
    if (!profileForm.displayName.trim()) return;

    setSaving(true);
    try {
      await updateUserProfile({ displayName: profileForm.displayName });
      // Show success feedback
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const copyApiKey = () => {
    navigator.clipboard.writeText('sk-ochtopus-xxxx-xxxx-xxxx-xxxxxxxxxxxx');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getInitials = (name: string | null, email: string | null) => {
    if (name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (email) {
      return email.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  const getPlanName = () => {
    // Can be fetched from Firestore user document
    return 'Free';
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="card">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-text-secondary text-sm">{t('dashboard.overview.completionsUsed')}</span>
                  <Sparkles size={18} className="text-accent" />
                </div>
                <div className="text-2xl font-bold">{usageData.completions.used.toLocaleString()}</div>
                <div className="text-text-tertiary text-sm mt-1">
                  / {usageData.completions.limit === 'Unlimited' ? t('dashboard.usage.unlimited') : usageData.completions.limit}
                </div>
              </div>

              <div className="card">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-text-secondary text-sm">{t('dashboard.overview.agentRequests')}</span>
                  <Zap size={18} className="text-warning" />
                </div>
                <div className="text-2xl font-bold">{usageData.agentRequests.used}</div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-2 bg-bg-elevated rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent rounded-full"
                      style={{
                        width: `${Math.min(
                          (usageData.agentRequests.used / usageData.agentRequests.limit) * 100,
                          100
                        )}%`,
                      }}
                    />
                  </div>
                  <span className="text-text-tertiary text-xs">{usageData.agentRequests.limit}</span>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-text-secondary text-sm">{t('dashboard.overview.timeSaved')}</span>
                  <Clock size={18} className="text-success" />
                </div>
                <div className="text-2xl font-bold">{usageData.timeSaved}</div>
                <div className="flex items-center gap-1 text-success text-sm mt-1">
                  <TrendingUp size={14} />
                  {t('dashboard.overview.thisMonth')}
                </div>
              </div>

              <div className="card">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-text-secondary text-sm">{t('dashboard.overview.linesWritten')}</span>
                  <BarChart3 size={18} className="text-info" />
                </div>
                <div className="text-2xl font-bold">{usageData.linesWritten}</div>
                <div className="text-text-tertiary text-sm mt-1">{t('dashboard.overview.total')}</div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="card">
              <h3 className="font-semibold mb-4">{t('dashboard.overview.recentActivity')}</h3>
              <div className="space-y-4">
                {activityData.length > 0 ? (
                  activityData.map((activity, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-2 border-b border-border-default last:border-0"
                    >
                      <div>
                        <div className="text-text-primary">{activity.action}</div>
                        <div className="text-text-tertiary text-sm">{activity.time}</div>
                      </div>
                      <ChevronRight size={18} className="text-text-tertiary" />
                    </div>
                  ))
                ) : (
                  <p className="text-text-secondary text-center py-8">
                    {t('dashboard.overview.noActivity')}
                  </p>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="card">
              <h3 className="font-semibold mb-4">{t('dashboard.overview.quickActions')}</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Link to="/download" className="btn btn-secondary w-full">
                  <Download size={18} />
                  {t('dashboard.overview.downloadApp')}
                </Link>
                <Link to="/docs" className="btn btn-secondary w-full">
                  {t('dashboard.overview.viewDocs')}
                </Link>
                <button className="btn btn-secondary w-full">
                  <RefreshCw size={18} />
                  {t('dashboard.overview.syncSettings')}
                </button>
                <Link to="/pricing" className="btn btn-primary w-full">
                  {t('dashboard.overview.upgradePlan')}
                </Link>
              </div>
            </div>
          </div>
        );

      case 'usage':
        return (
          <div className="space-y-6">
            <div className="card">
              <h3 className="font-semibold mb-4">{t('dashboard.usage.title')}</h3>
              <div className="h-64 bg-bg-elevated rounded-lg flex items-center justify-center text-text-tertiary">
                <div className="text-center">
                  <BarChart3 size={48} className="mx-auto mb-2 opacity-50" />
                  <p>{t('dashboard.usage.chartPlaceholder')}</p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="card">
                <h3 className="font-semibold mb-4">{t('dashboard.usage.completions')}</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{t('dashboard.usage.used')}</span>
                    <span className="font-medium">{usageData.completions.used.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{t('dashboard.usage.limit')}</span>
                    <span className="font-medium">{usageData.completions.limit === 'Unlimited' ? t('dashboard.usage.unlimited') : usageData.completions.limit}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{t('dashboard.usage.trend')}</span>
                    <span className="font-medium text-success">{usageData.completions.trend}</span>
                  </div>
                </div>
              </div>

              <div className="card">
                <h3 className="font-semibold mb-4">{t('dashboard.overview.agentRequests')}</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{t('dashboard.usage.used')}</span>
                    <span className="font-medium">{usageData.agentRequests.used}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{t('dashboard.usage.limit')}</span>
                    <span className="font-medium">{usageData.agentRequests.limit}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{t('dashboard.usage.trend')}</span>
                    <span className="font-medium text-success">{usageData.agentRequests.trend}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'settings':
        return (
          <div className="space-y-6">
            <div className="card">
              <h3 className="font-semibold mb-4">{t('dashboard.settings.profile')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-text-secondary text-sm mb-2">{t('dashboard.settings.displayName')}</label>
                  <input
                    type="text"
                    value={profileForm.displayName}
                    onChange={(e) => setProfileForm({ ...profileForm, displayName: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-sm mb-2">{t('dashboard.settings.email')}</label>
                  <input
                    type="email"
                    value={user.email || ''}
                    disabled
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default text-text-tertiary cursor-not-allowed"
                  />
                  <p className="text-text-tertiary text-xs mt-1">{t('dashboard.settings.emailCannotChange')}</p>
                </div>
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="btn btn-primary"
                >
                  {saving ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      {t('dashboard.settings.saving')}
                    </>
                  ) : (
                    t('dashboard.settings.saveChanges')
                  )}
                </button>
              </div>
            </div>

            <div className="card">
              <h3 className="font-semibold mb-4">{t('dashboard.settings.preferences')}</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{t('dashboard.settings.emailNotifications')}</div>
                    <div className="text-text-secondary text-sm">{t('dashboard.settings.emailNotificationsDesc')}</div>
                  </div>
                  <button className="w-12 h-6 rounded-full bg-accent relative">
                    <div className="w-5 h-5 rounded-full bg-white absolute right-0.5 top-0.5" />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{t('dashboard.settings.usageReports')}</div>
                    <div className="text-text-secondary text-sm">{t('dashboard.settings.usageReportsDesc')}</div>
                  </div>
                  <button className="w-12 h-6 rounded-full bg-border-default relative">
                    <div className="w-5 h-5 rounded-full bg-white absolute left-0.5 top-0.5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="card border-error/20">
              <h3 className="font-semibold mb-4 text-error">{t('dashboard.settings.dangerZone')}</h3>
              <p className="text-text-secondary text-sm mb-4">
                {t('dashboard.settings.dangerZoneDesc')}
              </p>
              <button className="btn btn-ghost text-error border border-error/20 hover:bg-error/10">
                {t('dashboard.settings.deleteAccount')}
              </button>
            </div>
          </div>
        );

      case 'api-keys':
        return (
          <div className="space-y-6">
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">{t('dashboard.apiKeys.title')}</h3>
                <button className="btn btn-primary btn-sm">{t('dashboard.apiKeys.generateNew')}</button>
              </div>

              <div className="p-4 bg-bg-elevated rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-text-secondary text-sm">{t('dashboard.apiKeys.defaultKey')}</span>
                  <span className="text-text-tertiary text-xs">
                    {t('dashboard.apiKeys.created')}: {new Date().toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-bg-card rounded font-mono text-sm overflow-hidden text-ellipsis">
                    {showApiKey
                      ? 'sk-ochtopus-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
                      : '••••••••••••••••••••••••••••••••'}
                  </code>
                  <button
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="p-2 hover:bg-bg-card rounded transition-colors"
                    title={showApiKey ? t('dashboard.apiKeys.hide') : t('dashboard.apiKeys.show')}
                  >
                    {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                  <button
                    onClick={copyApiKey}
                    className="p-2 hover:bg-bg-card rounded transition-colors"
                    title={t('dashboard.apiKeys.copy')}
                  >
                    {copied ? (
                      <Check size={18} className="text-success" />
                    ) : (
                      <Copy size={18} />
                    )}
                  </button>
                </div>
              </div>

              <p className="text-text-tertiary text-sm mt-4">
                {t('dashboard.apiKeys.description')}
              </p>
            </div>
          </div>
        );

      case 'billing':
        return (
          <div className="space-y-6">
            <div className="card">
              <h3 className="font-semibold mb-4">{t('dashboard.billing.currentPlan')}</h3>
              <div className="flex items-center justify-between p-4 bg-accent-glow rounded-lg border border-accent/20">
                <div>
                  <div className="text-lg font-semibold">{getPlanName() === 'Free' ? t('dashboard.billing.free') : getPlanName()}</div>
                  <div className="text-text-secondary text-sm">
                    {getPlanName() === 'Free' ? t('dashboard.billing.limitedFeatures') : '$20/month'}
                  </div>
                </div>
                <Link to="/pricing" className="btn btn-secondary btn-sm">
                  {t('dashboard.billing.upgrade')}
                </Link>
              </div>
            </div>

            <div className="card">
              <h3 className="font-semibold mb-4">{t('dashboard.billing.paymentMethod')}</h3>
              {getPlanName() === 'Free' ? (
                <div className="text-center py-8 text-text-secondary">
                  <CreditCard size={48} className="mx-auto mb-4 opacity-50" />
                  <p>{t('dashboard.billing.noPaymentMethod')}</p>
                  <p className="text-sm mt-2">{t('dashboard.billing.addPaymentMethod')}</p>
                  <Link to="/pricing" className="btn btn-primary mt-4">
                    {t('dashboard.billing.viewPlans')}
                  </Link>
                </div>
              ) : (
                <div className="flex items-center gap-4 p-4 bg-bg-elevated rounded-lg">
                  <div className="w-12 h-8 bg-bg-card rounded flex items-center justify-center">
                    <CreditCard size={20} className="text-text-tertiary" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">**** **** **** 4242</div>
                    <div className="text-text-tertiary text-sm">{t('dashboard.billing.expires')} 12/25</div>
                  </div>
                  <button className="btn btn-ghost btn-sm">{t('dashboard.billing.edit')}</button>
                </div>
              )}
            </div>

            <div className="card">
              <h3 className="font-semibold mb-4">{t('dashboard.billing.billingHistory')}</h3>
              {getPlanName() === 'Free' ? (
                <p className="text-text-secondary text-center py-8">{t('dashboard.billing.noBillingHistory')}</p>
              ) : (
                <div className="space-y-2">
                  {[
                    { date: 'Dec 1, 2024', amount: '$20.00', status: t('dashboard.billing.paid') },
                    { date: 'Nov 1, 2024', amount: '$20.00', status: t('dashboard.billing.paid') },
                    { date: 'Oct 1, 2024', amount: '$20.00', status: t('dashboard.billing.paid') },
                  ].map((invoice, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-3 border-b border-border-default last:border-0"
                    >
                      <div className="flex items-center gap-4">
                        <Calendar size={18} className="text-text-tertiary" />
                        <span>{invoice.date}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-medium">{invoice.amount}</span>
                        <span className="text-success text-sm">{invoice.status}</span>
                        <button className="btn btn-ghost btn-sm">{t('dashboard.billing.download')}</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="pt-24 min-h-screen bg-bg-base relative overflow-hidden">
      <DashboardBackground />
      <div className="container py-8 relative z-10">
        <div className="grid lg:grid-cols-[280px_1fr] gap-8">
          {/* Sidebar */}
          <aside className="space-y-6">
            {/* User Info */}
            <div className="card">
              <div className="flex items-center gap-4 mb-4">
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || 'User'}
                    className="w-14 h-14 rounded-full"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-accent-muted flex items-center justify-center text-accent text-xl font-bold">
                    {getInitials(user.displayName, user.email)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">
                    {user.displayName || 'User'}
                  </div>
                  <div className="text-text-secondary text-sm truncate">{user.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="px-2 py-1 rounded-full bg-accent-muted text-accent font-medium">
                  {getPlanName() === 'Free' ? t('dashboard.billing.free') : getPlanName()}
                </span>
                <span className="text-text-tertiary">
                  {t('dashboard.sidebar.since')}{' '}
                  {user.createdAt
                    ? new Date(user.createdAt).toLocaleDateString('en-US', {
                        month: 'long',
                        year: 'numeric',
                      })
                    : 'Recently'}
                </span>
              </div>
            </div>

            {/* Navigation */}
            <nav className="card p-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors',
                    activeTab === tab.id
                      ? 'bg-accent-muted text-accent'
                      : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                  )}
                >
                  <tab.icon size={18} />
                  {tab.label}
                </button>
              ))}

              <hr className="my-2 border-border-default" />

              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left text-error hover:bg-error/10 transition-colors"
              >
                <LogOut size={18} />
                {t('dashboard.sidebar.signOut')}
              </button>
            </nav>
          </aside>

          {/* Main Content */}
          <main>
            <h1 className="heading-lg mb-6">{tabs.find((t) => t.id === activeTab)?.label}</h1>
            {renderTabContent()}
          </main>
        </div>
      </div>
    </div>
  );
}
